import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  ShoppingBag,
  BarChart3,
  Users,
  Gift,
  Shield,
  FileText,
  Building2,
  Menu,
  X,
  ChevronDown,
  Receipt,
  Store,
  ShieldAlert,
  Activity,
  Settings,
  Bell
} from 'lucide-react'
import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { useQuery } from '@tanstack/react-query'

import { cn } from '../lib/utils'
import { useSessionStore } from '../store/auth/auth.store'
import { authService } from '../services/auth.service'
import { LogOut, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useEffect } from 'react'
import { layoutService } from '../services/layout.service'
import { useDeviceStore } from '../store/auth/auth.store'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { notificationService } from '../services/notification.service'
import { useSettingsBranding } from '../hooks/useSettingsBranding'

//CONSTANTE PARA OCULTAR MODULOS EN DESARROLLO Y MOSTRAR SOLO LOS DE PRODUCCION
const SHOW_ONLY_PRODUCTION_MODULES =
  import.meta.env.VITE_SHOW_ONLY_PRODUCTION_MODULES === 'true'

interface LayoutProps {
  children: ReactNode
}



const navigation = [
  {
    name: 'Panel General',
    href: '/',
    icon: LayoutDashboard,
    production: false,
  },
  {
    name: 'Configuración',
    icon: Settings,
    production: true,
    children: [
      { name: 'Plataforma', href: '/configuracion/plataforma' },
      { name: 'Impuestos', href: '/impuestos' },
      { name: 'Unidad de Medida', href: '/unidades-medida' },

    ],
  },
  {
    name: 'Punto de Venta',
    href: '/pos',
    icon: ShoppingCart,
    production: false,
  },
  {
    name: 'Registro de Ventas',
    href: '/ventas',
    icon: Receipt,
    production: false,
  },
  {
    name: 'Inventario',
    icon: Package,
    production: true,
    children: [
      { name: 'Inventario', href: '/inventario' },
      { name: 'Categorías', href: '/categorias' },
      { name: 'Departamentos', href: '/departamentos' },
      { name: 'Tipos de Producto', href: '/tipos-producto' },
    ],
  },
  {
    name: 'Compras y Proveedores',
    href: '/compras',
    icon: ShoppingBag,
    production: false,
  },
  {
    name: 'Reportes',
    href: '/reportes',
    icon: BarChart3,
    production: false
  },
  { name: 'Clientes', href: '/clientes', icon: Users, production: false },
  { name: 'Programa de Lealtad', href: '/lealtad', icon: Gift, production: false },
  {
    name: 'Logs del Sistema',
    icon: Activity,
    production: true,
    children: [
      { name: 'Logs de Accesos', href: '/logs-accesos' },
      { name: 'Logs de Acciones', href: '/logs-acciones' },
    ],
  },
  {
    name: 'Notificaciones',
    href: '/notificaciones',
    icon: Bell,
    production: true,
  },
  {
    name: 'Usuarios y Permisos',
    href: '/usuarios',
    icon: Shield,
    production: true,
  },
  {
    name: 'Sucursales',
    href: '/sucursales',
    icon: Building2,
    production: true,
  },
  {
    name: 'Módulo Regulatorio',
    href: '/regulatorio',
    icon: FileText,
    production: false
  },
  {
    name: 'Dashboard Sucursales',
    href: '/dashboard-sucursales',
    icon: Store,
    production: false
  },
]

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const [branches, setBranches] = useState<{ id: number, name: string }[]>([])
  const [selectedBranch, setSelectedBranch] = useState<{ id: number, name: string } | null>(null)
  const [openMenus, setOpenMenus] = useState<string[]>([])

  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { token, user, logout, activeBranchId, setActiveBranchId, isRevoked, revokeMessage, clearRevoked, setRevoked } = useSessionStore()
  const { branding } = useSettingsBranding({ mode: 'private', enabled: Boolean(token) })

  const filteredNavigation = SHOW_ONLY_PRODUCTION_MODULES
    ? navigation.filter(item => item.production)
    : navigation

  console.log('SHOW_ONLY_PRODUCTION_MODULES', SHOW_ONLY_PRODUCTION_MODULES)
  console.log('filteredNavigation', filteredNavigation)

  const [branchToChange, setBranchToChange] = useState<{
    id: number
    name: string
  } | null>(null)

  const { data: unreadNotifications = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationService.getUnreadCount,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    let isMounted = true
    /*
      Servicio del layout para obtener las sucursales asignadas al usuario
      se agregan para gestionar la sucursal activa en la sesion del usuario
      en caso de que la sucursal activa no sea valida se muestra un mensaje de error
      y se permite seleccionar otra sucursal
      
    */
    layoutService.getMySucursales().then(data => {
      if (!isMounted) return
      setBranches(data)

      if (data.length === 0) {
        setRevoked('No tienes sucursales activas asignadas en este momento.')
        return
      }

      const currentActiveId = useSessionStore.getState().activeBranchId
      const isCurrentValid = currentActiveId && data.some(b => b.id === currentActiveId)

      if (!isCurrentValid) {
        const trustedDevice = useDeviceStore.getState().trustedDevice
        const defaultBranch = data.find(b => b.id === trustedDevice?.sucursal_id) || data[0]
        setActiveBranchId(defaultBranch.id)
        setSelectedBranch(defaultBranch)

        // Si ya teníamos un sucursal activa pero fue desactivada
        if (currentActiveId !== null) {
          window.location.reload()
        }
      } else {
        const current = data.find(b => b.id === currentActiveId)
        if (current) setSelectedBranch(current)
      }
    }).catch(e => {
      console.error(e)
      toast.error('Error al cargar sucursales asignadas')
    })

    return () => { isMounted = false }
  }, [logout, setActiveBranchId])

  useEffect(() => {
    const activeParents = filteredNavigation
      .filter(item => item.children?.some(child => location.pathname === child.href))
      .map(item => item.name)

    setOpenMenus(prev => Array.from(new Set([...prev, ...activeParents])))
  }, [location.pathname])

  const toggleMenu = (name: string) => {
    setOpenMenus(prev =>
      prev.includes(name)
        ? prev.filter(n => n !== name)
        : [...prev, name]
    )
  }

  /*
    Maneja el cierre de sesion del usuario
    llama al servicio de authService.logout() para cerrar sesion
    y limpia la sesion del usuario
    muestra un mensaje de exito
    y redirige al usuario a la pagina de inicio
  */
  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await authService.logout()
    } catch (e) {
      console.error(e)
    } finally {
      logout()
      setIsLoggingOut(false)
      toast.success('Sesión cerrada correctamente')
    }
  }

  /*
    Maneja el cambio de sucursal
    llama a la funcion setActiveBranchId para establecer la nueva sucursal activa
    y recarga la pagina
  */
  const confirmBranchChange = () => {
    if (!branchToChange) return

    setActiveBranchId(branchToChange.id)
    window.location.reload()
  }

  if (isRevoked) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <ShieldAlert className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Acceso Suspendido</h2>
          <p className="text-slate-500 mb-8">{revokeMessage || 'Se ha revocado tu acceso a la sucursal actual.'}</p>

          <button
            onClick={() => {
              if (branches.length === 0) {
                logout()
                window.location.href = '/'
              } else {
                clearRevoked()
                window.location.reload()
              }
            }}
            className="w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white transition-colors hover:bg-slate-800"
          >
            {branches.length === 0 ? 'Cerrar sesión' : 'Actualizar sesión'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform border-r border-slate-200 bg-white transition-transform duration-200 ease-in-out lg:static lg:inset-0 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="border-b border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <img src={branding.logoMainUrl} alt={branding.companyName} />
              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-md p-1 hover:bg-slate-100 lg:hidden"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Branch Selector */}
            <div className="relative mt-3">
              <button
                onClick={() => branches.length > 1 && setBranchDropdownOpen(!branchDropdownOpen)}
                disabled={branches.length <= 1}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 transition-colors',
                  branches.length > 1 ? 'hover:bg-slate-100 cursor-pointer' : 'opacity-80 cursor-default'
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-600" />
                  <span className="text-sm text-slate-700">
                    {selectedBranch?.name || 'Cargando...'}
                  </span>
                </div>
                {branches.length > 1 && <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>

              {branchDropdownOpen && (
                <div className="absolute top-full right-0 left-0 z-10 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg">
                  {branches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => {
                        if (branch.id !== activeBranchId) {
                          setBranchToChange(branch)
                        } else {
                          setBranchDropdownOpen(false)
                        }
                      }}
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm first:rounded-t-lg last:rounded-b-lg hover:bg-slate-50',
                        selectedBranch?.id === branch.id &&
                        'bg-primary/10 text-primary',
                      )}
                    >
                      {branch.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {filteredNavigation.map((item) => {
              if (item.children) {
                const isActive = item.children.some(child => location.pathname === child.href)
                const isOpen = openMenus.includes(item.name)

                return (
                  <Collapsible
                    key={item.name}
                    open={isOpen}
                    onOpenChange={() => toggleMenu(item.name)}
                    className="space-y-1"
                  >
                    <CollapsibleTrigger
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg px-3 py-2 transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-700 hover:bg-slate-100',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{item.name}</span>
                      </div>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-1">
                      <div className="relative ml-3 border-l-2 border-slate-200 pl-3 space-y-0.5">
                        {item.children.map((child, idx) => {
                          const isChildActive = location.pathname === child.href
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              style={{
                                animationDelay: `${idx * 40}ms`,
                                animationFillMode: 'both',
                              }}
                              className={cn(
                                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-150',
                                'animate-in fade-in slide-in-from-left-2',
                                isChildActive
                                  ? 'bg-primary/15 text-primary font-medium'
                                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                              )}
                            >
                              <span
                                className={cn(
                                  'h-1.5 w-1.5 rounded-full shrink-0 transition-colors',
                                  isChildActive ? 'bg-primary' : 'bg-slate-300',
                                )}
                              />
                              {child.name}
                            </Link>
                          )
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )
              }

              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-slate-700 hover:bg-slate-100',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <item.icon className="h-5 w-5" />
                    <span className="truncate text-sm font-medium">{item.name}</span>
                  </span>
                  {item.href === '/notificaciones' && unreadNotifications > 0 && (
                    <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {unreadNotifications > 99 ? '99+' : unreadNotifications}
                    </span>
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="border-t border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 overflow-hidden items-center justify-center rounded-full bg-primary/10">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-sm font-medium text-primary">
                    {user?.name?.substring(0, 2).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="truncate text-sm font-medium text-slate-900">
                  {user?.name || 'Usuario'}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {user?.role?.name || 'Vendedor'}
                </div>
              </div>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center justify-center rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cerrar sesión"
              >
                {isLoggingOut ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <LogOut className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile header */}
        <div className="sticky top-0 z-40 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-md p-1 hover:bg-slate-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            <img src={branding.logoMainUrl} alt={branding.companyName} className="h-7" />
            <Link
              to="/notificaciones"
              className="relative rounded-md p-1 hover:bg-slate-100"
              aria-label="Abrir notificaciones"
            >
              <Bell className="h-6 w-6" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </Link>
          </div>
        </div>

        {children}
      </main>

      {branchToChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              Cambiar sucursal
            </h3>

            <p className="mt-2 text-sm text-slate-600">
              ¿Deseas cambiar a {' '}
              <span className="font-medium">
                {branchToChange.name}
              </span>
              ?
            </p>

            <p className="mt-2 text-xs text-slate-500">
              La aplicación se recargará para actualizar la información de la
              sucursal seleccionada.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setBranchToChange(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>

              <button
                onClick={confirmBranchChange}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}
